import { useTheme } from "@mui/material/styles"
import isEqual from "lodash-es/isEqual"
import { FC, memo } from "react"

import { Card } from "../card"
import { Chip } from "../chip"
import { Headline, HeadlineProps } from "../headline"
import { Icon, IconProps } from "../icon"
import { NextImage, NextImageProps } from "../image"
import { LinkProps } from "../link"
import { RichText } from "../rich-text"

import styles from "./PostTeaser.module.scss"

import { create } from "@/helpers/bem"
import { calculateWordCount } from "@/helpers/calculation"
import { Variants } from "@/types/variants"

const bem = create(styles, "PostTeaser")

export type PostTeaserVariant = Variants

export type PostTeaserHeadline = {
  content: string
} & Omit<HeadlineProps, "children">

export type PostTeaserImage = NextImageProps

export type PostTeaserRedirect = LinkProps & {
  label?: string
  icon?: IconProps
}

export type PostTeaserProps = {
  className?: string
  variant?: PostTeaserVariant
  readCount?: number
  title: PostTeaserHeadline
  content?: string
  hideCategory?: boolean
  category?: string
  date?: string
  metaDate?: string
  image?: PostTeaserImage
  onClick?: () => void
  redirect?: PostTeaserRedirect
}

export const PostTeaser: FC<PostTeaserProps> = memo(
  ({
    className,
    variant = "primary",
    image,
    readCount,
    title,
    date,
    metaDate,
    hideCategory,
    category,
    content,
    onClick,
    redirect,
    ...props
  }) => {
    const theme = useTheme()
    const themeModifier = {
      [theme.palette.mode]: true,
    }
    const wordCount = calculateWordCount(content)
    const customColor = isEqual(variant, "white") ? "inherit" : variant
    return (
      <article
        itemScope
        className={bem(undefined, undefined, className)}
        itemType="http://schema.org/BlogPosting"
      >
        <Card
          variant={isEqual(customColor, "inherit") ? "white" : customColor}
          {...props}
          animated
          className={bem("card__container")}
          contentClassName={bem("card")}
          customAnimation="bottom-top"
          redirect={redirect}
          onClick={onClick}
        >
          <header>
            {image && (
              <div className={bem("image__wrapper")}>
                <div className={bem("meta")}>
                  {category && !hideCategory && (
                    <Chip
                      aria-label={`Category ${category}`}
                      color={customColor}
                      label={category}
                      classes={{
                        root: bem("meta__category"),
                        label: bem("meta__category__label"),
                      }}
                    />
                  )}
                  {readCount !== undefined && (
                    <Chip
                      aria-label={`Read ${readCount} times`}
                      color="white"
                      icon={<Icon name="Eye" size="sm" />}
                      label={
                        <p
                          itemScope
                          itemProp="interactionStatistic"
                          itemType="https://schema.org/InteractionCounter"
                        >
                          <meta
                            content="https://schema.org/ReadAction"
                            itemProp="interactionType"
                          />
                          <span itemProp="userInteractionCount">
                            {readCount}
                          </span>
                        </p>
                      }
                    />
                  )}
                </div>
                <NextImage
                  {...(image as NextImageProps)}
                  fill
                  captionClassName={bem("image__caption")}
                  className={bem("image")}
                  containerClassName={bem("image")}
                />
              </div>
            )}
            <Headline
              size="md"
              type="h3"
              {...title}
              className={bem("headline", title?.className)}
              variant={title?.variant ?? customColor}
            >
              {title?.content}
            </Headline>
          </header>
          {content && (
            <div className={bem("card__content")} itemProp="articleBody">
              <RichText className={bem("content")}>{content}</RichText>
            </div>
          )}
          <div className={bem("card__footer")}>
            {redirect?.label && (
              <span className={bem("link")}>
                <Icon
                  color={theme.palette?.[customColor].main}
                  name="ArrowRightCircle"
                  size="xs"
                  {...redirect?.icon}
                  className={bem(
                    "link__icon",
                    undefined,
                    redirect?.icon?.className,
                  )}
                />
                {redirect.label}
              </span>
            )}
            {date && (
              <p
                aria-label={`Published at ${date}`}
                className={bem("date", themeModifier)}
                itemProp="datePublished"
              >
                <time dateTime={metaDate} itemProp="datePublished">
                  {date}
                </time>
              </p>
            )}
          </div>
        </Card>
        {wordCount > 0 && (
          <meta content={String(wordCount)} itemProp="wordCount" />
        )}
        {category && <meta content={category} itemProp="articleSection" />}
      </article>
    )
  },
)

PostTeaser.displayName = "PostTeaser"
